<template>
  <!-- listen to "keyup", but also "change" to update when the user selects a country -->
  <input
    :id="id"
    ref="inputRef"
    type="tel"
    v-bind="filteredAttrs"
    @change="onChange"
    @keyup="onChange"
  />
</template>

<script lang="ts">

import { defineComponent } from 'vue';
import intlTelInput from 'intl-tel-input';
import 'intl-tel-input/build/css/intlTelInput.css';
import { subsetObject } from '@/shared';

const utilsScript = 'https://cdn.jsdelivr.net/npm/intl-tel-input@23.9.1/build/js/utils.js';

interface IPhoneInputState {
  inputData: Record<string, any> | null
}

export default defineComponent({
  // Vue 3 auto-applies fallthrough attrs (and listeners, now merged into
  // $attrs) to the single root. We bind an explicitly filtered subset instead,
  // so disable automatic inheritance to avoid double-binding.
  inheritAttrs: false,

  props: {
    value: {
      type: String,
      default: '',
    },
    /**
     * Vue 3 v-model binding. Kept alongside the legacy `value`/`input` pair so
     * both `v-model` (modelValue/update:modelValue) and explicit `:value`+`@input`
     * consumers work. `undefined` means the parent did not bind v-model, so we
     * fall back to `value` (see the `boundValue` computed).
     */
    modelValue: {
      type: String,
      default: undefined,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    isInternational: {
      type: Boolean,
      default: true,
    },
    id: {
      type: String,
    },
  },

  emits: ['input', 'valid', 'update:modelValue'],

  data(): IPhoneInputState {
    return {
      inputData: null,
    };
  },

  computed: {
    /**
     * The externally-bound value, preferring the Vue 3 `modelValue` (v-model)
     * when present and falling back to the legacy `value` prop otherwise.
     */
    boundValue(): string {
      return this.modelValue !== undefined ? this.modelValue : this.value;
    },
    filteredAttrs() {
      // Vue 3 removed $listeners and merges v-on handlers into $attrs as onXxx
      // keys. Exclude the attrs and events this component handles internally
      // (change/keyup/input) so they don't double-bind on the raw <input>;
      // everything else falls through.
      return subsetObject(this.$attrs, ['value', 'type', 'disabled', 'onChange', 'onKeyup', 'onInput']);
    },
  },

  watch: {
    boundValue: {
      immediate: true,
      handler() {
        this.setValue();
      },
    },
  },

  methods: {
    setValue() {
      // It's a hack where mounted methods and immediate watcher handlers can read `$refs`. $refs is undefined otherwise.
      setTimeout(() => {
        if (!this.inputData) {
          this.inputData = intlTelInput(this.$refs.inputRef as HTMLInputElement, {
            utilsScript,
            allowDropdown: this.isInternational,
            containerClass: 'iti-container',
            dropdownContainer: document.body,
            formatAsYouType: true,
            formatOnDisplay: true,
            // i18n: {}, // If we want to add i18n in the future
            // Note that the area code is taken into consideration when determining the flag
            initialCountry: 'ca',
            countryOrder: ['ca', 'us'],
            separateDialCode: this.isInternational,
            strictMode: true,
          });
        }

        // for redundancy
        if (this.isInternational) {
          this.inputData.setCountry('ca');
        }

        if (this.disabled) {
          this.inputData.setDisabled(true);
        }

        this.inputData.setNumber(this.boundValue);
      }, 0);
    },

    onChange() {
      if (!this.inputData) {
        return;
      }

      const phoneNumber = this.inputData.getNumber(
        this.isInternational ? intlTelInput.utils?.numberFormat.E164 : intlTelInput.utils?.numberFormat.NATIONAL,
      );
      const rawVal = this.inputData.telInput?.value?.trim() ?? '';
      const isValid = this.inputData.isValidNumber();

      if (isValid || !rawVal.length) {
        this.$emit('input', phoneNumber);
        this.$emit('update:modelValue', phoneNumber);
      }

      this.$emit('valid', rawVal.length > 0 ? isValid : true);
    },
  },
});
</script>

<style lang="scss">

.iti-container > input {
  outline: none;
}

</style>
